import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  constituencies: string[];
  permissions: Record<string, boolean>;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
}

interface AppState {
  sidebarOpen: boolean;
  theme: 'light' | 'dark' | 'system';
  toggleSidebar: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
}

interface VoterFilterState {
  filters: {
    search: string;
    constituency: string;
    baagam: string;
    verificationStatus: string;
    votingPreference: string;
    ageRange: [number, number];
  };
  setFilter: (key: string, value: any) => void;
  clearFilters: () => void;
  setFilters: (filters: Partial<VoterFilterState['filters']>) => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      immer((set, get) => ({
        user: null,
        isAuthenticated: false,
        isLoading: false,

        login: async (email: string, password: string) => {
          set((state) => {
            state.isLoading = true;
          });

          try {
            const response = await fetch('/api/auth/login', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ email, password }),
            });

            if (!response.ok) {
              const error = await response.json();
              console.error('Login failed:', error.error);
              set((state) => {
                state.isLoading = false;
              });
              return false;
            }

            const authData = await response.json();

            // Store tokens in localStorage
            localStorage.setItem('accessToken', authData.accessToken);
            localStorage.setItem('refreshToken', authData.refreshToken);

            set((state) => {
              state.user = authData.user;
              state.isAuthenticated = true;
              state.isLoading = false;
            });

            return true;
          } catch (error) {
            console.error('Login error:', error);
            set((state) => {
              state.isLoading = false;
            });
            return false;
          }
        },

        logout: async () => {
          try {
            const token = localStorage.getItem('accessToken');

            if (token) {
              await fetch('/api/auth/logout', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
              });
            }

            // Clear localStorage
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');

            set((state) => {
              state.user = null;
              state.isAuthenticated = false;
            });
          } catch (error) {
            console.error('Logout error:', error);
          }
        },

        checkSession: async () => {
          const token = localStorage.getItem('accessToken');

          if (!token) {
            set((state) => {
              state.isAuthenticated = false;
              state.user = null;
            });
            return;
          }

          try {
            const response = await fetch('/api/auth/me', {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
            });

            if (response.ok) {
              const userData = await response.json();
              set((state) => {
                state.user = userData.user;
                state.isAuthenticated = true;
              });
            } else {
              // Clear invalid session
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
              set((state) => {
                state.isAuthenticated = false;
                state.user = null;
              });
            }
          } catch (error) {
            console.error('Failed to check session:', error);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            set((state) => {
              state.isAuthenticated = false;
              state.user = null;
            });
          }
        },

        updateUser: (userData: Partial<User>) => {
          set((state) => {
            if (state.user) {
              Object.assign(state.user, userData);
            }
          });
        },
      })),
      {
        name: 'auth-storage',
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    { name: 'auth-store' }
  )
);

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      immer((set) => ({
        sidebarOpen: true,
        theme: 'system',

        toggleSidebar: () => {
          set((state) => {
            state.sidebarOpen = !state.sidebarOpen;
          });
        },

        setTheme: (theme: 'light' | 'dark' | 'system') => {
          set((state) => {
            state.theme = theme;
          });
        },
      })),
      {
        name: 'app-storage',
      }
    ),
    { name: 'app-store' }
  )
);

export const useVoterFilterStore = create<VoterFilterState>()(
  devtools(
    persist(
      immer((set) => ({
        filters: {
          search: '',
          constituency: '',
          baagam: '',
          verificationStatus: '',
          votingPreference: '',
          ageRange: [18, 100],
        },

        setFilter: (key: string, value: any) => {
          set((state) => {
            (state.filters as any)[key] = value;
          });
        },

        clearFilters: () => {
          set((state) => {
            state.filters = {
              search: '',
              constituency: '',
              baagam: '',
              verificationStatus: '',
              votingPreference: '',
              ageRange: [18, 100],
            };
          });
        },

        setFilters: (filters: Partial<VoterFilterState['filters']>) => {
          set((state) => {
            Object.assign(state.filters, filters);
          });
        },
      })),
      {
        name: 'voter-filters-storage',
      }
    ),
    { name: 'voter-filters-store' }
  )
);

// Selectors for efficient re-renders
export const useAuth = () => useAuthStore((state) => state);
export const useApp = () => useAppStore((state) => state);
export const useVoterFilters = () => useVoterFilterStore((state) => state);

// Derived selectors
export const useCurrentUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useUserPermissions = () => useAuthStore((state) => state.user?.permissions || {});
export const useUserConstituencies = () => useAuthStore((state) => state.user?.constituencies || []);
export const useUserRole = () => useAuthStore((state) => state.user?.role || '');